import {isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {AlphaCatalogue} from '../api/digitalCertificateApi';
import {VehicleV2ResponseBody} from '../api/types/onlineOfferTypes';
import {getAlphaCatalogueLabel} from './getAlphaCatalogueLabel';
import {getAlphaCataloguePropertyValue} from './getAlphaCataloguePropertyValue';
import {
  ElectricalConsumptionData,
  FuelConsumptionData,
  getElectricalConsumptionData,
  getFuelConsumptionData,
} from './getFuelConsumptionData';
import {
  getVehicleConsumptionVariant,
  shouldShowElectricalConsumptionInDetails,
  shouldShowFuelConsumptionInDetails,
  VehicleConsumptionVariant,
} from './getVehicleConsumptionVariant';

export interface DetailsDataBundle {
  vin: string | Nullish;
  firstRegistration: string | Nullish;
  countryOfOrigin: string | Nullish;
  typeOfColour: string | Nullish;
  manufacturerColourName: string | Nullish;
  exteriorColour:
    | {
        name: string | Nullish;
        code: string | Nullish;
      }
    | Nullish;
  interiorColour:
    | {
        name: string | Nullish;
        code: string | Nullish;
      }
    | Nullish;
  interiorColourName: string | Nullish;
  interiorMaterial: string | Nullish;
  numberOfSeats: number | Nullish;
  numberOfDoors: string | Nullish;
  numberOfKeys: number | Nullish;
  numberOfOwners: number | Nullish;
  hybridType: string | Nullish;
  fuelType: string | Nullish;
  secondaryFuelType: string | Nullish;
  emissionClass: string | Nullish;
  combustionEnginePower: string | Nullish;
  electricalEnginePower: string | Nullish;
  maximalEnginePower: string | Nullish;
  engineDisplacement: string | Nullish;
  numberOfPistons: number | Nullish;
  numberOfGears: number | Nullish;
  consumptionVariant: VehicleConsumptionVariant;
  fuelConsumptionData: FuelConsumptionData | Nullish;
  electricalConsumptionData: ElectricalConsumptionData | Nullish;
}

export const getDataForDetails = (
  vehicle: VehicleV2ResponseBody | Nullish,
  alphaCatalogue: AlphaCatalogue | Nullish
): DetailsDataBundle | null => {
  if (!vehicle || !alphaCatalogue) {
    return null;
  }
  const countryOfOrigin = vehicle.originCountryName ?? vehicle.originCountry;
  const typeOfColour = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'color_type',
    key: vehicle.exteriorColorSpecification,
    alphaCatalogue,
  });
  const manufacturerColourName = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'body_color',
    key: vehicle.manufacturerColorName,
    alphaCatalogue,
  });
  const exteriorColourName = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'body_color',
    key: vehicle.exteriorColor?.value,
    alphaCatalogue,
  });
  const exteriorColorCode = getAlphaCataloguePropertyValue({
    vehicleType: vehicle.type,
    outerPropertyName: 'body_color',
    innerPropertyName: 'color_code',
    key: vehicle.exteriorColor?.value,
    alphaCatalogue,
  });
  const interiorColourName = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'interior_color',
    key: vehicle.interiorColor?.value,
    alphaCatalogue,
  });
  const interiorColorCode = getAlphaCataloguePropertyValue({
    vehicleType: vehicle.type,
    outerPropertyName: 'interior_color',
    innerPropertyName: 'color_code',
    key: vehicle.interiorColor?.value,
    alphaCatalogue,
  });
  const interiorMaterial = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'interior_material',
    key: vehicle.interiorMaterial,
    alphaCatalogue,
  });
  const numberOfDoors = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'door_count',
    key: vehicle.doorCountCategory,
    alphaCatalogue,
  });
  const hybridType = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'hybrid_type',
    key: vehicle.hybridType,
    alphaCatalogue,
  });
  const fuelType = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'fuel_type',
    key: vehicle.fuelType,
    alphaCatalogue,
  });
  const secondaryFuelType = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'fuel_type',
    key: vehicle.secondaryFuelType,
    alphaCatalogue,
  });
  const emissionClass = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'emission_class',
    key: vehicle.emissionClass,
    alphaCatalogue,
  });

  const consumptionVariant = getVehicleConsumptionVariant(vehicle);
  const fuelConsumptionData = shouldShowFuelConsumptionInDetails(consumptionVariant)
    ? getFuelConsumptionData(vehicle, alphaCatalogue)
    : null;
  const electricalConsumptionData = shouldShowElectricalConsumptionInDetails(consumptionVariant)
    ? getElectricalConsumptionData(vehicle, alphaCatalogue)
    : null;

  const combustionEnginePower = vehicle.powerCombustionEngine
    ? `${vehicle.powerCombustionEngine} kW`
    : null;

  const electricalEnginePower = vehicle.powerElectricEngine
    ? `${vehicle.powerElectricEngine} kW`
    : null;

  const maximalEnginePower = vehicle.maximalPower ? `${vehicle.maximalPower} kW` : null;

  const engineDisplacement = vehicle.engineVolume ? `${vehicle.engineVolume} ccm` : null;

  return {
    vin: vehicle.vin,
    firstRegistration: vehicle.firstRegistrationOn,
    countryOfOrigin,
    typeOfColour,
    manufacturerColourName,
    exteriorColour: isNotNilOrEmpty(exteriorColourName)
      ? {
          name: exteriorColourName,
          code: typeof exteriorColorCode === 'boolean' ? null : exteriorColorCode,
        }
      : null,
    interiorColour: isNotNilOrEmpty(interiorColourName)
      ? {
          name: interiorColourName,
          code: typeof interiorColorCode === 'boolean' ? null : interiorColorCode,
        }
      : null,
    interiorColourName: vehicle.interiorColorName,
    interiorMaterial,
    numberOfSeats: vehicle.seatCount,
    numberOfDoors,
    numberOfKeys: vehicle.primaryKeyCount,
    numberOfOwners: vehicle.ownerCount,
    hybridType,
    fuelType,
    secondaryFuelType,
    emissionClass,
    combustionEnginePower,
    maximalEnginePower,
    electricalEnginePower,
    engineDisplacement,
    numberOfPistons: vehicle.cylinderCount,
    numberOfGears: vehicle.gearCount,
    consumptionVariant,
    fuelConsumptionData,
    electricalConsumptionData,
  };
};
